/**
 * /admin/kautionen — Kautionen & Sicherheiten.
 *
 * ONE PAGE, TWO OPPOSITE BALANCES
 * -------------------------------
 * German calls both of these "Kaution", and the product confused them until K1:
 *
 *   Mieterkautionen                     what tenants placed with US   — a liability
 *   Sicherheitsleistungen bei Vermietern what WE placed with owners   — an asset
 *
 * They are shown side by side because an operator wants both in view, and they
 * are never summed, never share a bucket, and never appear in each other's
 * figures. There is deliberately no combined total anywhere on this page.
 *
 * CURRENT STATE, NOT A PERIOD
 * ---------------------------
 * This page ignores the month picker that governs the finance surfaces. Neither
 * endpoint accepts a period and neither underlying table keeps validity history,
 * so the honest answer is "as of today" and the date is stamped by the backend in
 * the Swiss business calendar (Regel 51) — never by the device clock.
 *
 * K3a SCOPE
 * ---------
 * Header, KPI row, banners and the two section shells with their summary strips.
 * The row-level tables, filters, status badges, provider labels, the data-quality
 * sidebar and the drilldowns are K3b, and nothing here fakes them: an unbuilt
 * table is absent, not stubbed with example rows.
 */
import React, { useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

import { chf, chfPrecise } from "../../components/admin/companyFinance/financeFormat";
import DepositsDataQualityCard from "./deposits/DepositsDataQualityCard";
import DepositsFilterBar, {
  DepositsFilterSelect,
} from "./deposits/DepositsFilterBar";
import DepositsInfoBanner from "./deposits/DepositsInfoBanner";
import DepositsKpiRow from "./deposits/DepositsKpiRow";
import DepositsSummaryCard from "./deposits/DepositsSummaryCard";
import LandlordDepositDetailModal from "./deposits/LandlordDepositDetailModal";
import LandlordDepositsTable from "./deposits/LandlordDepositsTable";
import TenantDepositDetailModal from "./deposits/TenantDepositDetailModal";
import TenantDepositsTable from "./deposits/TenantDepositsTable";
import { filterLandlordItems, filterTenantItems } from "./deposits/depositsFilters";
import { securityStateFilterLabel } from "./deposits/depositsLabels";
import {
  selectDepositsAsOfDate,
  selectLandlordDepositKpis,
  selectTenantDepositKpis,
  selectTenantUnresolvedSummary,
} from "./deposits/depositsSelectors";
import useDepositsData from "./deposits/useDepositsData";

/** ISO date to Swiss civil notation. The value already IS the Swiss business day
 *  (the backend stamped it); this only changes its punctuation. */
function formatAsOf(iso) {
  if (typeof iso !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null;
}

/**
 * The status filter's options, in the order an operator works through them:
 * what still needs doing first, then what is settled.
 *
 * Labels come from `securityStateFilterLabel` rather than being typed here.
 * They read differently from a row's badge — "Erhalten / Hinterlegt" names both
 * words `held` can produce, because the option selects rows of every type — but
 * they are DERIVED from the same mapping, so a renamed label cannot leave a
 * dropdown behind saying the old word.
 */
const TENANT_STATUS_OPTIONS = [
  { value: "all", label: "Alle Status" },
  ...["pending", "held", "active_guarantee", "unresolved", "returned", "excluded"].map(
    (value) => ({ value, label: securityStateFilterLabel(value) })
  ),
];

export default function AdminDepositsPage() {
  const { tenant, landlord } = useDepositsData();

  /**
   * K4c.2: which row's detail is open, or null.
   *
   * The ROW is the state, not an id: `TenantSecurityItem` and
   * `LandlordDepositItem` already carry every fact their modal shows, so
   * holding the item means opening a detail costs no request for facts — only
   * the one for documents, and only once the modal is actually open.
   *
   * Deliberately not in `useDepositsData`: that hook fetches the two
   * aggregates this page renders, and documents are neither. Putting document
   * state there is how a page-level N+1 starts.
   */
  const [tenantDetail, setTenantDetail] = useState(null);
  const [landlordDetail, setLandlordDetail] = useState(null);

  const tenantKpis = selectTenantDepositKpis(tenant.data);
  const landlordKpis = selectLandlordDepositKpis(landlord.data);
  const unresolved = selectTenantUnresolvedSummary(tenant.data);
  const asOf = formatAsOf(selectDepositsAsOfDate(tenant.data, landlord.data));

  // "Nothing here" only once a source has actually answered. While loading, or
  // after a failure, an empty state would assert something the page cannot know.
  const tenantEmpty =
    !tenant.loading && !tenant.error && (tenantKpis.total ?? 0) <= 0;
  const landlordEmpty =
    !landlord.loading && !landlord.error && (landlordKpis.total ?? 0) <= 0;

  // Filter state lives here so both panels can be reset independently and the
  // tables stay presentational. It never touches the figures above: the KPI row
  // and both summary strips are the backend's snapshot of the whole portfolio,
  // and a total that shrinks while you type is not a total.
  const [tenantQuery, setTenantQuery] = useState("");
  const [tenantType, setTenantType] = useState("all");
  const [tenantStatus, setTenantStatus] = useState("all");
  const [landlordQuery, setLandlordQuery] = useState("");
  const [landlordType, setLandlordType] = useState("all");

  // Memoised for their IDENTITY, not for the cost of the fallback: `?? []`
  // hands back a fresh array on every render, which would make the filter memos
  // below recompute each time and defeat the point of having them.
  const tenantItems = useMemo(() => tenant.data?.items || [], [tenant.data]);
  const landlordItems = useMemo(() => landlord.data?.items || [], [landlord.data]);

  const tenantRows = useMemo(
    () => filterTenantItems(tenantItems, {
      query: tenantQuery, type: tenantType, status: tenantStatus,
    }),
    [tenantItems, tenantQuery, tenantType, tenantStatus]
  );
  const landlordRows = useMemo(
    () => filterLandlordItems(landlordItems, { query: landlordQuery, type: landlordType }),
    [landlordItems, landlordQuery, landlordType]
  );

  const tenantFiltered = tenantItems.length > 0 && tenantRows.length === 0;
  const landlordFiltered = landlordItems.length > 0 && landlordRows.length === 0;

  const tenantStats = [
    {
      label: "Gesamtbetrag",
      value: chf(tenantKpis.total),
      emphasis: true,
      testId: "deposits-tenant-stat-total",
    },
    { label: "Barkaution", value: chf(tenantKpis.cash), testId: "deposits-tenant-stat-cash" },
    {
      label: "Versicherung",
      value: chf(tenantKpis.insurance),
      testId: "deposits-tenant-stat-insurance",
    },
    { label: "Bankdepot", value: chf(tenantKpis.bank), testId: "deposits-tenant-stat-bank" },
    {
      label: "Nicht zugeordnet",
      value: chf(unresolved ? unresolved.amount : 0),
      testId: "deposits-tenant-stat-unresolved",
    },
  ];

  const landlordStats = [
    {
      label: "Gesamtbetrag",
      value: chf(landlordKpis.total),
      emphasis: true,
      testId: "deposits-landlord-stat-total",
    },
    {
      label: "Versicherung",
      value: chf(landlordKpis.insurance),
      testId: "deposits-landlord-stat-insurance",
    },
    { label: "Bankdepot", value: chf(landlordKpis.bank), testId: "deposits-landlord-stat-bank" },
    {
      label: "Bareinzahlung",
      value: chf(landlordKpis.cash),
      testId: "deposits-landlord-stat-cash",
    },
    {
      // Reported beside the securities, never inside their total: an annual
      // expense and a placed balance are different dimensions.
      label: "Jahresprämie p.a.",
      value: chfPrecise(landlordKpis.annualPremium),
      testId: "deposits-landlord-stat-premium",
    },
  ];

  return (
    <div className="space-y-4">
      <header>
        <Link
          /* The Finanzen tab is local state on the overview page and has no URL
             of its own, so the target carries the tab in navigation state and
             that page opens on it. Without this the link would land on
             Übersicht and quietly contradict its own label. */
          to="/admin"
          state={{ tab: "finanzen" }}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
        >
          <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
          Zurück zu Finanzen
        </Link>

        <h1 className="mt-2 text-[22px] font-semibold tracking-tight text-foreground">
          Kautionen &amp; Sicherheiten
        </h1>
        <p
          data-testid="deposits-as-of"
          className="mt-0.5 text-[12.5px] text-muted-foreground"
        >
          {asOf ? `Aktueller Stand · ${asOf}` : "Aktueller Stand"}
        </p>
      </header>

      <div aria-live="polite">
        <DepositsKpiRow
          tenant={tenantKpis}
          landlord={landlordKpis}
          tenantError={tenant.error}
          landlordError={landlord.error}
          tenantLoading={tenant.loading}
          landlordLoading={landlord.loading}
          unresolved={unresolved}
          onRetryTenant={tenant.reload}
          onRetryLandlord={landlord.reload}
        />
      </div>

      <DepositsInfoBanner testId="deposits-info-banner" tone="info">
        Mieterkautionen sind Sicherheiten Ihrer Mieter und werden getrennt von den
        Sicherheiten geführt, die Sie selbst gegenüber Vermietern stellen. Kautionen
        und Sicherheitsbeträge sind weder Umsatz noch Betriebskosten.
      </DepositsInfoBanner>

      <div className="grid grid-cols-1 gap-4 2xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          <DepositsSummaryCard
            testId="deposits-tenant-panel"
            title="Mieterkautionen"
            subline="Sicherheiten, die Mieter gegenüber uns gestellt haben"
            stats={tenantStats}
            loading={tenant.loading}
            error={tenant.error}
            onRetry={tenant.reload}
            isEmpty={tenantEmpty}
            emptyMessage="Keine aktuellen Mietersicherheiten"
          >
            {tenantItems.length > 0 ? (
              <div className="mt-3.5 space-y-3">
                <DepositsFilterBar
                  testId="deposits-tenant-filters"
                  searchValue={tenantQuery}
                  onSearchChange={setTenantQuery}
                  searchLabel="Mieterkautionen durchsuchen"
                  searchPlaceholder="Mieter, Referenz, Objekt …"
                  resultLabel={`${tenantRows.length} von ${tenantItems.length}`}
                >
                  <DepositsFilterSelect
                    label="Kautionsart"
                    value={tenantType}
                    onChange={setTenantType}
                    options={[
                      { value: "all", label: "Alle Arten" },
                      { value: "cash", label: "Barkaution" },
                      { value: "insurance", label: "Versicherung" },
                      { value: "bank", label: "Bankdepot" },
                      { value: "untyped", label: "Nicht zugeordnet" },
                    ]}
                  />
                  <DepositsFilterSelect
                    label="Status"
                    value={tenantStatus}
                    onChange={setTenantStatus}
                    options={TENANT_STATUS_OPTIONS}
                  />
                </DepositsFilterBar>

                <TenantDepositsTable
                  items={tenantRows}
                  onOpenDetails={setTenantDetail}
                />

                {tenantFiltered ? (
                  <p
                    data-testid="deposits-tenant-no-match"
                    className="rounded-lg border border-dashed border-border p-4 text-center text-[12px] text-muted-foreground"
                  >
                    Keine Kautionen entsprechen den aktuellen Filtern.
                  </p>
                ) : null}
              </div>
            ) : null}
          </DepositsSummaryCard>

          <DepositsSummaryCard
            testId="deposits-landlord-panel"
            title="Sicherheitsleistungen bei Vermietern"
            subline="Sicherheiten, die wir gegenüber Vermietern gestellt haben"
            stats={landlordStats}
            loading={landlord.loading}
            error={landlord.error}
            onRetry={landlord.reload}
            isEmpty={landlordEmpty}
            emptyMessage="Keine Sicherheitsleistungen erfasst"
          >
            {landlordItems.length > 0 ? (
              <div className="mt-3.5 space-y-3">
                <DepositsFilterBar
                  testId="deposits-landlord-filters"
                  searchValue={landlordQuery}
                  onSearchChange={setLandlordQuery}
                  searchLabel="Sicherheitsleistungen durchsuchen"
                  searchPlaceholder="Einheit, Adresse, Ort …"
                  resultLabel={`${landlordRows.length} von ${landlordItems.length}`}
                >
                  <DepositsFilterSelect
                    label="Sicherheitsart"
                    value={landlordType}
                    onChange={setLandlordType}
                    options={[
                      { value: "all", label: "Alle Arten" },
                      { value: "insurance", label: "Versicherung" },
                      { value: "bank", label: "Bankdepot" },
                      { value: "cash", label: "Bareinzahlung" },
                    ]}
                  />
                </DepositsFilterBar>

                <LandlordDepositsTable
                  items={landlordRows}
                  onOpenDetails={setLandlordDetail}
                />

                {landlordFiltered ? (
                  <p
                    data-testid="deposits-landlord-no-match"
                    className="rounded-lg border border-dashed border-border p-4 text-center text-[12px] text-muted-foreground"
                  >
                    Keine Sicherheitsleistungen entsprechen den aktuellen Filtern.
                  </p>
                ) : null}
              </div>
            ) : null}
          </DepositsSummaryCard>
        </div>

        <DepositsDataQualityCard
          tenantQuality={tenant.data?.data_quality}
          landlordQuality={landlord.data?.data_quality}
          tenantUnavailable={Boolean(tenant.error)}
          landlordUnavailable={Boolean(landlord.error)}
          loading={tenant.loading || landlord.loading}
        />
      </div>

      <DepositsInfoBanner testId="deposits-bottom-info" tone="note">
        Kautionen und Sicherheiten werden nicht als Umsatz behandelt. Nur die von uns
        bezahlten Versicherungsprämien auf Vermieterseite werden als Betriebskosten
        berücksichtigt.
      </DepositsInfoBanner>

      {/* Rendered only while open, so the document fetch inside cannot run for
          a row nobody asked about. Closing clears the item, which is also what
          makes a reopen load a fresh list. */}
      {tenantDetail ? (
        <TenantDepositDetailModal
          item={tenantDetail}
          open
          onOpenChange={(next) => {
            if (!next) setTenantDetail(null);
          }}
        />
      ) : null}

      {landlordDetail ? (
        <LandlordDepositDetailModal
          item={landlordDetail}
          open
          onOpenChange={(next) => {
            if (!next) setLandlordDetail(null);
          }}
        />
      ) : null}
    </div>
  );
}
